import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UnlockFlow } from './useActionGuard';

/**
 * Extra required claims are client-side, dynamic preconditions layered *on top*
 * of whatever Casbin already requires for an (action, resource). Casbin gates the
 * real operation (e.g. `share:action`); an extra required claim adds a further
 * constraint the app enforces itself — the canonical case being "the user has set
 * both their familiar and allowed names before they may share".
 *
 * A constraint is a plain check function: it returns `null` when the claim is
 * currently held, or — when it is not — an unlock descriptor telling the guard how
 * to send the user to satisfy it. Keeping this out of Casbin (and out of the
 * backend-mirrored `ClaimsLevel`) is deliberate: these never touch the server.
 */
export interface ExtraRequiredClaimUnlock {
  /** Stable id for this claim, used to dedupe across guards in a container. */
  key: string;
  unlockFlow: UnlockFlow;
  /** Toast copy; supports `[bracketed]` toast-button text. */
  helpText: string;
  /** Label for the "return here" button the unlock destination may render. */
  returnFromUnlockText?: string;
}

/** Returns null when the claim is satisfied; an unlock descriptor when it is not. */
export type ExtraRequiredClaimCheck = () => ExtraRequiredClaimUnlock | null;

const constraintKey = (action: string, resource: string) =>
  `${action}:${resource}`;

interface ActionGuardConstraintsContextType {
  registerExtraRequiredClaim: (
    action: string,
    resource: string,
    check: ExtraRequiredClaimCheck
  ) => () => void;
  /**
   * Runs the registered checks for (action, resource) and returns the first unmet
   * claim's unlock descriptor, or null if all are satisfied / none registered.
   */
  getExtraRequiredClaimUnlock: (
    action: string,
    resource: string
  ) => ExtraRequiredClaimUnlock | null;
}

const ActionGuardConstraintsContext =
  createContext<ActionGuardConstraintsContextType | null>(null);

export const ActionGuardConstraintsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const registry = useRef<Map<string, Set<ExtraRequiredClaimCheck>>>(new Map());
  // Bumped on every register/unregister (including the re-registration a
  // constraint does when its inputs change, e.g. names being set). Feeds the
  // value memo below so context consumers re-render and re-evaluate their checks.
  const [version, setVersion] = useState(0);

  const registerExtraRequiredClaim = useCallback(
    (action: string, resource: string, check: ExtraRequiredClaimCheck) => {
      const key = constraintKey(action, resource);
      let checks = registry.current.get(key);
      if (!checks) {
        checks = new Set();
        registry.current.set(key, checks);
      }
      checks.add(check);
      setVersion(v => v + 1);

      return () => {
        const current = registry.current.get(key);
        if (!current) return;
        current.delete(check);
        if (current.size === 0) {
          registry.current.delete(key);
        }
        setVersion(v => v + 1);
      };
    },
    []
  );

  const getExtraRequiredClaimUnlock = useCallback(
    (action: string, resource: string): ExtraRequiredClaimUnlock | null => {
      const checks = registry.current.get(constraintKey(action, resource));
      if (!checks) return null;
      for (const check of Array.from(checks)) {
        const unlock = check();
        if (unlock) return unlock;
      }
      return null;
    },
    []
  );

  const value = useMemo(
    () => ({ registerExtraRequiredClaim, getExtraRequiredClaimUnlock }),
    // `version` intentionally participates: a new value identity on each
    // register/unregister forces consumers to re-run getExtraRequiredClaimUnlock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerExtraRequiredClaim, getExtraRequiredClaimUnlock, version]
  );

  return (
    <ActionGuardConstraintsContext.Provider value={value}>
      {children}
    </ActionGuardConstraintsContext.Provider>
  );
};

// No-op fallback so guards work even without a provider (e.g. isolated tests):
// nothing is registered, so no extra claims ever block.
const NOOP_CONTEXT: ActionGuardConstraintsContextType = {
  registerExtraRequiredClaim: () => () => {},
  getExtraRequiredClaimUnlock: () => null,
};

export function useActionGuardConstraints(): ActionGuardConstraintsContextType {
  return useContext(ActionGuardConstraintsContext) ?? NOOP_CONTEXT;
}
